import http from "./httpClient.js";

// Superadmin management of the public directory: /api/v1/admin/directory
// (the auth header is added by the shared http client).

const BASE = `/admin/directory`;
const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

// ---- Schools
export async function getSchools(params = {}) {
    const response = await http.get(`${BASE}/schools`, { params });
    return response.data;
}

export async function getSchool(schoolId) {
    const response = await http.get(`${BASE}/schools/${schoolId}`);
    return response.data;
}

export async function createSchool(data) {
    const response = await http.post(`${BASE}/schools`, data);
    return response.data;
}

export async function updateSchool(schoolId, data) {
    const response = await http.patch(`${BASE}/schools/${schoolId}`, data);
    return response.data;
}

export async function deleteSchool(schoolId) {
    await http.delete(`${BASE}/schools/${schoolId}`);
}

export async function verifyTimetable(schoolId) {
    const response = await http.patch(`${BASE}/schools/${schoolId}/verify-timetable`);
    return response.data;
}

/** @param {{ logo?: File, cover?: File }} files */
export async function uploadSchoolImages(schoolId, files) {
    const formData = new FormData();
    if (files.logo) formData.append('logo', files.logo);
    if (files.cover) formData.append('cover', files.cover);
    const response = await http.post(`${BASE}/schools/${schoolId}/images`, formData, multipart);
    return response.data;
}

// ---- Weekly classes
export async function createClass(schoolId, data) {
    const response = await http.post(`${BASE}/schools/${schoolId}/classes`, data);
    return response.data;
}

export async function updateClass(classId, data) {
    const response = await http.patch(`${BASE}/classes/${classId}`, data);
    return response.data;
}

export async function deleteClass(classId) {
    await http.delete(`${BASE}/classes/${classId}`);
}

// ---- Genres (global dance style catalog)
export async function getGenres() {
    const response = await http.get(`${BASE}/genres`);
    return response.data;
}

export async function createGenre(data) {
    const response = await http.post(`${BASE}/genres`, data);
    return response.data;
}

export async function updateGenre(genreId, data) {
    const response = await http.patch(`${BASE}/genres/${genreId}`, data);
    return response.data;
}

export async function deleteGenre(genreId) {
    await http.delete(`${BASE}/genres/${genreId}`);
}

// ---- Organizers
export async function getOrganizers() {
    const response = await http.get(`${BASE}/organizers`);
    return response.data;
}

export async function createOrganizer(data) {
    const response = await http.post(`${BASE}/organizers`, data);
    return response.data;
}

export async function updateOrganizer(organizerId, data) {
    const response = await http.patch(`${BASE}/organizers/${organizerId}`, data);
    return response.data;
}

export async function deleteOrganizer(organizerId) {
    await http.delete(`${BASE}/organizers/${organizerId}`);
}

export async function uploadOrganizerLogo(organizerId, file) {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await http.post(`${BASE}/organizers/${organizerId}/logo`, formData, multipart);
    return response.data;
}

// ---- Events
export async function getEvents(params = {}) {
    const response = await http.get(`${BASE}/events`, { params });
    return response.data;
}

export async function getEvent(eventId) {
    const response = await http.get(`${BASE}/events/${eventId}`);
    return response.data;
}

// ---- Per-date changes of a repeating event (dateKey: YYYY-MM-DD)
export async function updateOccurrence(eventId, dateKey, data) {
    const response = await http.patch(`${BASE}/events/${eventId}/occurrences/${dateKey}`, data);
    return response.data;
}

export async function resetOccurrence(eventId, dateKey) {
    const response = await http.delete(`${BASE}/events/${eventId}/occurrences/${dateKey}`);
    return response.data;
}

export async function uploadOccurrenceCover(eventId, dateKey, file) {
    const formData = new FormData();
    formData.append('cover', file);
    const response = await http.post(`${BASE}/events/${eventId}/occurrences/${dateKey}/cover`, formData, multipart);
    return response.data;
}

export async function createEvent(data) {
    const response = await http.post(`${BASE}/events`, data);
    return response.data;
}

export async function updateEvent(eventId, data) {
    const response = await http.patch(`${BASE}/events/${eventId}`, data);
    return response.data;
}

export async function deleteEvent(eventId) {
    await http.delete(`${BASE}/events/${eventId}`);
}

export async function uploadEventCover(eventId, file) {
    const formData = new FormData();
    formData.append('cover', file);
    const response = await http.post(`${BASE}/events/${eventId}/cover`, formData, multipart);
    return response.data;
}

// ---- CSV import
/**
 * @param {'schools'|'classes'} type
 * @param {File} file
 * @param {{ dryRun?: boolean, replace?: boolean }} options
 */
export async function importCsv(type, file, { dryRun = false, replace = false } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await http.post(`${BASE}/import/${type}`, formData, {
        ...multipart,
        params: { dryRun, replace },
    });
    return response.data;
}

// ---- Inbox & stats
export async function getReports(params = {}) {
    const response = await http.get(`${BASE}/reports`, { params });
    return response.data;
}

export async function updateReport(reportId, status) {
    const response = await http.patch(`${BASE}/reports/${reportId}`, { status });
    return response.data;
}

export async function getClaims() {
    const response = await http.get(`/school-waitlist`, { params: { sort: '-createdAt', limit: 200 } });
    return response.data;
}

export async function updateClaim(entryId, status) {
    const response = await http.patch(`/school-waitlist/${entryId}`, { status });
    return response.data;
}

export async function getStats(days = 30) {
    const response = await http.get(`${BASE}/stats`, { params: { days } });
    return response.data;
}
